import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Popover, PopoverTrigger } from "@similarweb/ui-components";

import type { useAssignUsersToTeams } from "../../hooks/useAssignUsersToTeams";
import type { TeamListItem, TeamUser } from "../../types/teams.types";
import { areAllUsersInTeam } from "../../utils/areAllUsersInTeam";

import { AssignTeamsCombobox } from "./AssignTeamsCombobox";
import { StyledAssignUsersPopoverContent } from "./styles";

type AssignUsersToTeams = ReturnType<typeof useAssignUsersToTeams>;

type AssignUsersToTeamPickerProps = {
    teams: readonly TeamListItem[];
    users: TeamUser[];
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    triggerRef: RefObject<HTMLButtonElement | null>;
    onSuccess: () => void;
    assignUsersToTeams: AssignUsersToTeams["assignUsersToTeams"];
    isPending: AssignUsersToTeams["isPending"];
};

export const AssignUsersToTeamPicker = ({
    teams,
    users,
    isOpen,
    onOpenChange,
    triggerRef,
    onSuccess,
    assignUsersToTeams,
    isPending,
}: AssignUsersToTeamPickerProps) => {
    const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedTeamIds([]);
        }
    }, [isOpen]);

    const selectedUserIds = useMemo(() => users.map((user) => user.userId), [users]);

    const handleToggleTeam = useCallback((teamId: number) => {
        setSelectedTeamIds((current) =>
            current.includes(teamId) ? current.filter((id) => id !== teamId) : [...current, teamId],
        );
    }, []);

    const enabledSelectedTeamIds = useMemo(
        () =>
            selectedTeamIds.filter((teamId) => {
                const team = teams.find((entry) => entry.teamId === teamId);
                return team !== undefined && !areAllUsersInTeam(team, selectedUserIds);
            }),
        [selectedTeamIds, selectedUserIds, teams],
    );

    const handleSave = useCallback(() => {
        if (isPending || enabledSelectedTeamIds.length === 0) {
            return;
        }

        const teamIdsToAssign = enabledSelectedTeamIds;
        const usersToAssign = users;

        onOpenChange(false);

        void assignUsersToTeams({
            teamIds: teamIdsToAssign,
            users: usersToAssign,
            teams,
        }).then((success) => {
            if (success) {
                onSuccess();
            }
        });
    }, [assignUsersToTeams, enabledSelectedTeamIds, isPending, onOpenChange, onSuccess, teams, users]);

    const handlePopoverOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                onOpenChange(false);
            }
        },
        [onOpenChange],
    );

    const isSaveDisabled = enabledSelectedTeamIds.length === 0;

    return (
        <Popover
            open={isOpen}
            onOpenChange={handlePopoverOpenChange}
            placement="bottom-start"
            isClickOutsideEnabled
            initialFocus={-1}
        >
            <PopoverTrigger elementRef={triggerRef as RefObject<HTMLElement>} />
            <StyledAssignUsersPopoverContent data-automation="assign-users-to-team-picker">
                <AssignTeamsCombobox
                    teams={teams}
                    selectedTeamIds={selectedTeamIds}
                    onToggleTeam={handleToggleTeam}
                    selectedUserIds={selectedUserIds}
                    isOpen={isOpen}
                    onApply={handleSave}
                    isApplyDisabled={isSaveDisabled}
                    applyDataAutomation="assign-users-to-team-save"
                />
            </StyledAssignUsersPopoverContent>
        </Popover>
    );
};
